import { $pegApi } from '@/boot/peg-api'
import { phoneNumberNational } from '@/utils/formatter.util'
import { cloneDeep } from 'lodash-es'

const CONTACT_DEFAULTS = {
  GENERAL: { email: 'support@peg.com', displayName: 'Peg Support' },
  RETAILER: { email: 'retailersupport@peg.com', displayName: 'Peg Retailer Support' },
  RETURNS: { email: 'returnsupport@peg.com', displayName: 'Peg Returns Support' },
  MARKETPLACE: { email: 'customersupport@peg.com', displayName: 'Peg Customer Support' },
}

// add same phone number to all types since we only have one support line for now
for (const type in CONTACT_DEFAULTS) {
  CONTACT_DEFAULTS[type].phone = '+14022356035'
}

export const useAppConfigStore = defineStore('appConfigs', () => {
  const { featureFlags } = useFeatureFlagStore()

  const privacyPolicyLink = {
    href: 'https://www.peg.com/privacy',
    target: '_blank',
    rel: 'noopener noreferrer',
  }

  const termsOfServiceLink = {
    href: 'https://www.peg.com/terms',
    target: '_blank',
    rel: 'noopener noreferrer',
  }

  const retailerGuidesLink = {
    href: 'https://www.peg.com/retailers/integrations',
    target: '_blank',
    rel: 'noopener noreferrer',
  }

  // PEG-536: Canonical support contacts sourced from supportContacts API.
  const remoteContacts = ref(cloneDeep(CONTACT_DEFAULTS))
  let supportContactsFetching = false

  async function loadSupportContacts() {
    if (supportContactsFetching) return
    supportContactsFetching = true
    try {
      const res = await $pegApi.SupportContacts.getAll()
      const contacts = res?.contacts || []
      for (const c of contacts) {
        if (c.supportType && c.supportType in remoteContacts.value) {
          remoteContacts.value[c.supportType] = { ...remoteContacts.value[c.supportType], ...c }
        }
      }
    } catch {
      // allow retry on next access
    } finally {
      supportContactsFetching = false
    }
  }

  // Kick off load immediately; expose promise so consumers can await readiness.
  // Timeout after 2s so a slow/down API doesn't block app boot — defaults will be used.
  const supportContactsReady = Promise.race([loadSupportContacts(), new Promise(resolve => setTimeout(resolve, 2000))])

  const supportConfig = computed(() => {
    const resolve = type => ({
      ...remoteContacts.value[type],
      phone: phoneNumberNational(remoteContacts.value[type].phone),
    })

    return {
      general: resolve('GENERAL'),
      marketplace: resolve('MARKETPLACE'),
      retailer: resolve('RETAILER'),
      returns: resolve('RETURNS'),
    }
  })

  const checkoutConfig = computed(() => ({
    paymentGateway: featureFlags.checkoutPaymentGateway.value,
    paymentMethods: featureFlags.checkoutPaymentMethods.value,
  }))

  return {
    supportConfig,
    supportContactsReady,
    checkoutConfig,
    loadSupportContacts,
    privacyPolicyLink,
    termsOfServiceLink,
    retailerGuidesLink,
  }
})
